import { ref } from "vue";
import { getIpHomeAddress, getUserDetail } from "~/api/api";
import {
	LOCALE_REGION_COOKIE_KEY,
	TW_REGION_SEGMENT,
} from "~/utils/i18n-locale-routes";

/** 台湾 IP 时置顶展示的老师 UID（按展示顺序） */
export const TW_PRIORITY_TEACHER_UIDS = [
	"1778936842033070",
	"1786003204032293",
	"1786092727031133",
];

function getCountryCode(res) {
	return res?.data?.data?.country_code || res?.data?.country_code || res?.country_code || "";
}

function getTeacherUid(teacher) {
	return String(teacher?.teacher_uid || teacher?.uid || teacher?.id || "");
}

function normalizePriorityTeacher(data, uid) {
	if (!data) return null;
	return {
		...data,
		teacher_uid: data.teacher_uid || data.uid || uid,
	};
}

/** 仅对缺失的 UID 逐个查询详情 */
export async function fetchTwPriorityTeachersByUids(uids) {
	const targets = (uids || []).map(String).filter(Boolean);
	if (!targets.length) return [];

	const results = await Promise.all(
		targets.map(async uid => {
			try {
				const res = await getUserDetail({ uid });
				return normalizePriorityTeacher(res?.data, uid);
			} catch (error) {
				console.error(`fetchTwPriorityTeacher uid=${uid}:`, error);
				return null;
			}
		})
	);

	return results.filter(item => item && getTeacherUid(item));
}

/**
 * 台湾 IP / 调试参数时：
 * - 列表里已有的置顶老师：只重排到前面，不重复展示、不重复请求
 * - 列表里没有的：才单独查接口补上，再置顶
 */
export async function mergeTwPriorityTeachers(list, isTw) {
	const source = Array.isArray(list) ? list : [];
	if (!isTw) return source;

	const found = new Map();
	const rest = [];

	for (const teacher of source) {
		const uid = getTeacherUid(teacher);
		if (TW_PRIORITY_TEACHER_UIDS.includes(uid)) {
			if (!found.has(uid)) found.set(uid, teacher);
		} else {
			rest.push(teacher);
		}
	}

	const missingUids = TW_PRIORITY_TEACHER_UIDS.filter(uid => !found.has(uid));
	if (missingUids.length) {
		const fetched = await fetchTwPriorityTeachersByUids(missingUids);
		for (const teacher of fetched) {
			const uid = getTeacherUid(teacher);
			if (uid && !found.has(uid)) found.set(uid, teacher);
		}
	}

	const priority = TW_PRIORITY_TEACHER_UIDS.map(uid => found.get(uid)).filter(Boolean);
	return [...priority, ...rest];
}

export function useTwPriorityTeachers() {
	const isTwIp = ref(false);
	let detecting = null;

	function hasTwQueryFlag() {
		if (!import.meta.client) return false;
		try {
			const route = useRoute();
			return String(route.query?.ip || "").toLowerCase() === "tw";
		} catch {
			return false;
		}
	}

	async function detectTwIp() {
		if (!import.meta.client) return isTwIp.value;
		if (detecting) return detecting;

		detecting = (async () => {
			// 开发调试：?ip=tw 强制走台湾置顶逻辑
			if (hasTwQueryFlag()) {
				isTwIp.value = true;
				return true;
			}

			const localeRegionCookie = useCookie(LOCALE_REGION_COOKIE_KEY, {
				default: () => "",
				maxAge: 60 * 60 * 24 * 365,
				sameSite: "lax",
			});

			if (localeRegionCookie.value === TW_REGION_SEGMENT) {
				isTwIp.value = true;
				return true;
			}

			try {
				const res = await getIpHomeAddress();
				const isTw = String(getCountryCode(res)).trim().toUpperCase() === "TW";
				isTwIp.value = isTw;
				if (isTw) {
					localeRegionCookie.value = TW_REGION_SEGMENT;
				}
				return isTw;
			} catch (error) {
				console.error("detectTwIp error:", error);
				return false;
			}
		})();

		try {
			return await detecting;
		} finally {
			detecting = null;
		}
	}

	async function applyTwPriority(list) {
		return mergeTwPriorityTeachers(list, isTwIp.value);
	}

	return {
		isTwIp,
		detectTwIp,
		applyTwPriority,
	};
}
